import type { ReportVersion } from '@/types';

type Entries = ReportVersion['entries'];

const TIMELINE_START_MINUTES = 7 * 60;
const MINUTES_PER_DAY = 24 * 60;

export interface DraftActivity {
  id: string;
  text: string;
  minutes: number;
  tags: string[];
}

export interface DraftRow {
  id: string;
  projectId: string | null;
  projectName: string;
  minutes: number | null;
  startTime: string | null;
  endTime: string | null;
  activities: DraftActivity[];
}

interface ComparableRow {
  projectId: string | null;
  minutes: number;
  startTime: string | null;
  endTime: string | null;
  activities: ComparableActivity[];
}

interface ComparableActivity {
  text: string;
  minutes: number | null;
  tags: string[];
}

interface TimelineRange {
  row: DraftRow;
  startMinute: number;
  endMinute: number;
}

let sequence = 0;

function nextId(): string {
  sequence += 1;
  return `draft-${Date.now().toString(36)}-${sequence}`;
}

export function newActivity(): DraftActivity {
  return { id: nextId(), text: '', minutes: 0, tags: [] };
}

export function newRow(): DraftRow {
  return {
    id: nextId(),
    projectId: null,
    projectName: '',
    minutes: null,
    startTime: null,
    endTime: null,
    activities: [newActivity()],
  };
}

export function newTimelineRow(startTime = '09:00', endTime = '10:00'): DraftRow {
  return {
    ...newRow(),
    minutes: minutesBetween(startTime, endTime),
    startTime,
    endTime,
  };
}

export function rowsFromEntries(entries: Entries): DraftRow[] {
  return entries.map((entry) => ({
    id: nextId(),
    projectId: entry.projectId,
    projectName: entry.projectName,
    minutes: entry.startTime && entry.endTime ? entry.minutes : null,
    startTime: entry.startTime,
    endTime: entry.endTime,
    activities: entry.activities.map((activity) => ({
      id: activity.id || nextId(),
      text: activity.text,
      minutes: activity.minutes,
      tags: [...activity.tags],
    })),
  }));
}

export function entriesFromRows(rows: DraftRow[]): Entries {
  return rows.map((row) => ({
    projectId: row.projectId,
    projectName: row.projectName,
    minutes: rowMinutes(row),
    startTime: row.startTime,
    endTime: row.endTime,
    activities: row.activities.map(({ id, text, minutes, tags }) => ({ id, text, minutes, tags })),
  }));
}

export function rowMinutes(row: DraftRow): number {
  if (row.minutes != null) {
    return normalizedMinutes(row.minutes);
  } else {
    return row.activities.reduce((sum, activity) => sum + normalizedMinutes(activity.minutes), 0);
  }
}

export function totalMinutes(rows: DraftRow[]): number {
  return rows.reduce((sum, row) => sum + rowMinutes(row), 0);
}

export function minutesBetween(startTime: string | null, endTime: string | null): number {
  const start = minutesFromTime(startTime);
  const end = minutesFromTime(endTime);

  if (start == null || end == null) {
    return 0;
  } else if (end >= start) {
    return end - start;
  } else {
    return 24 * 60 - start + end;
  }
}

export function minutesFromTime(value: string | null): number | null {
  if (!value) return null;

  const [hours, minutes] = value.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return hours * 60 + minutes;
}

export function timeRangeErrors(rows: DraftRow[]): Map<string, string> {
  const errors = new Map<string, string>();
  const ranges: TimelineRange[] = [];

  rows.forEach((row) => {
    if (!row.startTime && !row.endTime) return;
    if (!row.startTime || !row.endTime) {
      errors.set(row.id, 'completá desde y hasta');
      return;
    }

    const startMinute = minutesFromTime(row.startTime);
    const endMinute = minutesFromTime(row.endTime);
    if (startMinute == null || endMinute == null) {
      errors.set(row.id, 'ingresá horarios válidos');
      return;
    }
    if (startMinute === endMinute) {
      errors.set(row.id, 'desde y hasta deben ser distintos');
      return;
    }

    const timelineStart = timelineOffsetMinutes(startMinute);
    let timelineEnd = timelineOffsetMinutes(endMinute);
    if (timelineEnd < timelineStart) timelineEnd += MINUTES_PER_DAY;

    ranges.push({ row, startMinute: timelineStart, endMinute: timelineEnd });
  });

  ranges.forEach((first, index) => {
    ranges.slice(index + 1).forEach((second) => {
      if (first.startMinute < second.endMinute && second.startMinute < first.endMinute) {
        errors.set(first.row.id, `se superpone con ${formatTimeRange(second.row)}`);
        errors.set(second.row.id, `se superpone con ${formatTimeRange(first.row)}`);
      }
    });
  });

  return errors;
}

export function formatTimeRange(row: Pick<DraftRow, 'startTime' | 'endTime'>): string {
  if (!row.startTime || !row.endTime) return '';

  const startMinute = minutesFromTime(row.startTime);
  const endMinute = minutesFromTime(row.endTime);
  const suffix =
    startMinute != null && endMinute != null && endMinute < startMinute ? ' (+1 día)' : '';

  return `${row.startTime}–${row.endTime}${suffix}`;
}

export function rowsHaveChanges(left: DraftRow[], right: DraftRow[]): boolean {
  return JSON.stringify(comparableRows(left)) !== JSON.stringify(comparableRows(right));
}

function comparableRows(rows: DraftRow[]): ComparableRow[] {
  return rows.map((row) => ({
    projectId: row.projectId,
    minutes: rowMinutes(row),
    startTime: row.startTime,
    endTime: row.endTime,
    activities: row.activities.map((activity) => ({
      text: activity.text.trim(),
      minutes: row.minutes == null ? normalizedMinutes(activity.minutes) : null,
      tags: [...activity.tags].sort(),
    })),
  }));
}

export function nextTimelineStart(rows: DraftRow[]): string {
  const latestEnd = rows
    .map((row) => minutesFromTime(row.endTime))
    .filter((value) => value != null)
    .sort((left, right) => timelineOffsetMinutes(right) - timelineOffsetMinutes(left))[0];

  return timeFromMinutes(latestEnd ?? 9 * 60);
}

export function nextTimelineEnd(startTime: string): string {
  return timeFromMinutes((minutesFromTime(startTime) ?? 9 * 60) + 60);
}

export function timeFromMinutes(totalMinutes: number): string {
  const bounded = ((Math.trunc(totalMinutes) % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(bounded / 60);
  const minutes = bounded % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function normalizedMinutes(minutes: number): number {
  if (Number.isFinite(minutes)) {
    return Math.max(Math.trunc(minutes), 0);
  } else {
    return 0;
  }
}

function timelineOffsetMinutes(minutes: number): number {
  if (minutes >= TIMELINE_START_MINUTES) {
    return minutes - TIMELINE_START_MINUTES;
  } else {
    return minutes + 24 * 60 - TIMELINE_START_MINUTES;
  }
}
